import { FPAOneLayout } from '@amzn/gft-fpa-one-layout';
import { useAurigaContext } from '@context/AurigaContext';
import { useUserContext } from '@context/UserContext';
import { useAuth } from '@utils/auth/AuthProvider';
import { getAurigaSettings } from '@utils/cookies';
import { ReactNode, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import AurigaRoutes from 'src/routes/AurigaRoutes';
// Shared library imports

/**
 * RouteTracker Component
 *
 * This component is responsible for tracking route changes
 * and logging page views using the RUM logger.
 *
 * @returns This component doesn't render any UI
 */
const RouteTracker = (): null => {
  const location = useLocation();
  const { rumLogger } = useAurigaContext();
  const path = location.pathname + location.search;

  // Start a load timing measurement on every route change; completed by useRecordLoadComplete in each route
  useEffect(() => {
    if (rumLogger) {
      rumLogger.startOnLoad(path);
    }
  }, [path, rumLogger]);

  return null;
};

/**
 * AurigaLayout Component
 *
 * This component serves as the main layout for the Auriga application.
 * It uses the FPAOneLayout component from the GFTFunctionalUILibrary
 * and sets up the application routing.
 *
 * @returns {ReactNode} The AurigaLayout component
 */
const AurigaLayout = (): ReactNode => {
  const { isAuthenticated, user: authUser } = useAuth();
  const { username } = useUserContext();
  const aurigaSettings = getAurigaSettings();
  const { stageName } = aurigaSettings;
  // Constructs the FPA One link based on stage for use in header
  const fpaOneLink =
    stageName === 'prod'
      ? 'https://fpaone.fgbs.amazon.dev/'
      : `https://${stageName === 'development' ? 'alpha' : stageName}.fpaone.fgbs.amazon.dev/`;

  // Construct UserData expected for FPAOneLayout components
  const userData = {
    userAlias: username,
    userFirstName: authUser?.attributes['custom:FIRST_NAME'],
    userLastName: authUser?.attributes['custom:LAST_NAME'],
  };

  // Top menu content
  const topMenuContent = (
    <div style={{ padding: '16px', color: 'white' }}>
      {/* This is where we could put added custom Auriga content */}
      Custom Auriga App Menu
    </div>
  );

  return (
    <FPAOneLayout
      userData={userData}
      isUserAuthorized={isAuthenticated}
      topMenuContent={topMenuContent}
      dashboardName='Auriga'
      logoLink={fpaOneLink}
      showMenuIcon={false}
      showFeedback={false}
      contentPadding
      contentPaddingAmount='0px' // Override to increase or reduce padding from default 16px. Deliberately set to '0px' here to support Route-based padding in AurigaRoutes.tsx.
      // Keep the app shell viewport-locked so `.main-content` is the sole vertical
      // scroller. Auriga has a fixed side panel and internally-scrolling grids that
      // must not scroll away with the page (the shared shell's default 'shell' mode
      // makes the whole shell scroll, which scrolled the side panel out of view).
      scrollMode='content'
      onFeedbackClick={() => {
        // TODO: Add feedback action for Auriga

        window.alert('Feedback currently disabled');
      }}
    >
      <Router
        future={{
          v7_startTransition: true, // These v7 additions resolve console warnings
          v7_relativeSplatPath: true,
        }}
      >
        <RouteTracker />
        <AurigaRoutes />
      </Router>
    </FPAOneLayout>
  );
};

export default AurigaLayout;
